import React, { useState } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { useConfig } from "@/contexts/ConfigContext";
import { ROLE_BADGE } from "@/lib/perms";
import {
  LayoutDashboard, KanbanSquare, Users, ListChecks, BarChart3, LogOut, Languages,
  Briefcase, ShieldCheck, SlidersHorizontal, ScrollText, Menu, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const SPRING = { type: "spring", stiffness: 420, damping: 32 };

const NavItem = ({ to, icon: Icon, label, testid, onNavigate }) => (
  <NavLink to={to} data-testid={testid} onClick={onNavigate} className="relative">
    {({ isActive }) => (
      <span
        className={`relative flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
          isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-900"
        }`}
      >
        {isActive && (
          <motion.span
            layoutId="nav-pill"
            transition={SPRING}
            className="absolute inset-0 rounded-full bg-white shadow-[0_2px_12px_rgba(15,23,42,0.10)] ring-1 ring-slate-900/5"
          />
        )}
        <Icon className="w-4 h-4 relative z-10" />
        <span className="relative z-10 whitespace-nowrap">{label}</span>
      </span>
    )}
  </NavLink>
);

const AppShell = () => {
  const { user, logout, can } = useAuth();
  const { t, lang, toggle } = useLang();
  const cfg = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const items = [
    { to: "/dashboard", icon: LayoutDashboard, label: t("dashboard"), testid: "nav-dashboard" },
    { to: "/pipeline", icon: KanbanSquare, label: t("pipeline"), testid: "nav-pipeline" },
    { to: "/clients", icon: Users, label: t("clients"), testid: "nav-clients" },
    { to: "/activities", icon: ListChecks, label: t("activities"), testid: "nav-activities" },
    { to: "/reports", icon: BarChart3, label: t("reports"), testid: "nav-reports" },
  ];
  if (can("deals:approve")) items.push({ to: "/approvals", icon: ShieldCheck, label: t("approvals"), testid: "nav-approvals" });
  if (can("audit:read")) items.push({ to: "/audit-log", icon: ScrollText, label: t("audit_log"), testid: "nav-audit-log" });
  if (can("users:manage")) items.push({ to: "/admin", icon: SlidersHorizontal, label: t("administration"), testid: "nav-admin" });

  return (
    <div className="min-h-screen bg-[#F6F7FB] crm-grain">
      <header className="sticky top-0 z-40 px-3 sm:px-6 pt-3">
        <div className="mx-auto max-w-[1600px] glass-bar rounded-2xl px-3 sm:px-4 h-16 flex items-center gap-3">
          <NavLink to="/dashboard" className="flex items-center gap-2 pr-2 shrink-0" data-testid="brand-home-link">
            <motion.div
              whileHover={{ rotate: -8, scale: 1.06 }}
              transition={SPRING}
              className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-[0_6px_16px_rgba(234,88,12,0.35)]"
            >
              <Briefcase className="w-4 h-4 text-white" />
            </motion.div>
            <div className="hidden sm:block leading-tight">
              <div className="font-bold text-[15px] tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {cfg?.settings?.company_name || t("app_name")}
              </div>
              <div className="text-[10px] text-slate-400 uppercase tracking-[0.14em]">{t("tagline")}</div>
            </div>
          </NavLink>

          <nav className="hidden lg:flex items-center gap-1 flex-1 px-2 overflow-x-auto crm-scroll">
            {items.map((i) => <NavItem key={i.to} {...i} />)}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <span
              data-testid="current-role-badge"
              className={`hidden sm:inline text-[11px] font-semibold px-2.5 py-1 rounded-full border ${ROLE_BADGE[user?.role] || ROLE_BADGE.viewer}`}
            >
              {t(user?.role || "viewer")}
            </span>
            <Button onClick={toggle} variant="outline" size="sm" data-testid="language-toggle-button"
              className="gap-1.5 h-9 rounded-full border-slate-200 bg-white/70 text-slate-700">
              <Languages className="w-4 h-4" />
              <span className="font-semibold text-xs">{lang.toUpperCase()}</span>
            </Button>
            <NavLink to="/profile" data-testid="nav-profile" className="shrink-0">
              <Avatar className="w-9 h-9 ring-2 ring-white shadow-sm">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-slate-900 text-white text-xs">
                  {user?.name?.slice(0, 2)?.toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
            </NavLink>
            <button onClick={handleLogout} data-testid="logout-button" aria-label="logout"
              className="hidden lg:flex p-2 rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
            <button onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-nav-toggle"
              className="lg:hidden p-2 rounded-full text-slate-600 hover:bg-slate-100">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={SPRING}
              className="lg:hidden mx-auto max-w-[1600px] mt-2 glass-bar rounded-2xl p-2 flex flex-col gap-1"
              data-testid="mobile-nav-panel"
            >
              {items.map((i) => <NavItem key={i.to} {...i} onNavigate={() => setMobileOpen(false)} />)}
              <button onClick={handleLogout} data-testid="logout-button-mobile"
                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold text-rose-600 hover:bg-rose-50">
                <LogOut className="w-4 h-4" /> {t("logout")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="px-3 sm:px-6 py-6 max-w-[1600px] w-full mx-auto">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
};

export default AppShell;
